import { CSSProperties, ReactElement, ReactNode, useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";
import { DataTable } from "./DataTable";
import { TableToolbar, TableFooter, FilterBar } from "@/components/molecules/table";
import { TableLoader } from "@/components/ui/pulsing-loader";

interface DataTableViewProps<TData> {
  // Data
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  
  // Pagination
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  hidePagination?: boolean; // Hide pagination controls (for non-paginated tables)
  
  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  
  // Sorting
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  
  // Actions
  toolbarActions?: ReactNode;
  onRowClick?: (data: TData) => void;
  getRowClassName?: (data: TData) => string;
  getRowStyle?: (data: TData) => CSSProperties;
  /** Wraps each rendered <tr>, e.g. with a right-click menu */
  rowWrapper?: (data: TData, tr: ReactElement) => ReactElement;
  /** Sticky header + capped table height (pair with columns that set meta.stickyLeft) */
  stickyHeader?: boolean;
  
  // Filters
  filters?: ReactNode;
  
  // Messages
  emptyMessage?: string;
  
  // Loading
  isLoading?: boolean;
}

export function DataTableView<TData>({
  data,
  columns,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  hidePagination = false,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  sorting,
  onSortingChange,
  toolbarActions,
  onRowClick,
  getRowClassName,
  getRowStyle,
  rowWrapper,
  stickyHeader,
  filters,
  emptyMessage,
  isLoading = false,
}: DataTableViewProps<TData>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);

  // Sync external sorting state if provided
  useEffect(() => {
    if (sorting) {
      setInternalSorting(sorting);
    }
  }, [sorting]);

  // Auto-reset to page 1 if current page exceeds total pages
  // This handles cases where page size changes make current page invalid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      onPageChange(1);
    }
  }, [currentPage, totalPages, onPageChange]);

  // Wrap onPageSizeChange to reset page synchronously
  const handlePageSizeChange = (size: number) => {
    onPageSizeChange(size);
    // Reset to page 1 when page size changes to avoid invalid page state
    if (currentPage > 1) {
      onPageChange(1);
    }
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting: sorting !== undefined ? sorting : internalSorting,
    },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function"
          ? updater(sorting !== undefined ? sorting : internalSorting)
          : updater;
      
      if (onSortingChange) {
        onSortingChange(newSorting);
      } else {
        setInternalSorting(newSorting);
      }
    },
    manualSorting: onSortingChange !== undefined,
  });

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border">
        {filters && <FilterBar>{filters}</FilterBar>}
        
        <TableToolbar
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          isLoading={isLoading}
          actions={toolbarActions}
        />

        {isLoading ? (
          <TableLoader message="Cargando datos..." />
        ) : (
          <DataTable
            table={table}
            onRowClick={onRowClick}
            getRowClassName={getRowClassName}
            getRowStyle={getRowStyle}
            rowWrapper={rowWrapper}
            stickyHeader={stickyHeader}
            emptyMessage={emptyMessage}
          />
        )}

        {!hidePagination && (
          <TableFooter
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={pageSizeOptions}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
}
